import { AlertCircle, CheckCircle2, Clock } from 'lucide-react';

interface HealthStatus {
  failed_last_hour: number;
  failed_last_24h: number;
  failed_last_7d: number;
  last_failure_at: string | null;
  checked_at: string;
}

interface SystemHealthStatusProps {
  status: HealthStatus | null;
  loading: boolean;
  onViewErrors?: () => void;
}

export default function SystemHealthStatus({ status, loading, onViewErrors }: SystemHealthStatusProps) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
          <div className="flex-1">
            <div className="h-3 bg-gray-200 rounded w-1/3 mb-1.5"></div>
            <div className="h-2.5 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <div className="flex items-center gap-2.5">
          <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-yellow-900">Status niet beschikbaar</h3>
            <p className="text-xs text-yellow-700 mt-0.5">Kan systeem status niet ophalen</p>
          </div>
        </div>
      </div>
    );
  }

  const hasErrors = status.failed_last_hour > 0 || status.failed_last_24h > 0 || status.failed_last_7d > 0;
  const totalErrors = status.failed_last_hour + status.failed_last_24h + status.failed_last_7d;

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'zojuist';
    if (diffMins < 60) return `${diffMins} minuten geleden`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} uur geleden`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} dagen geleden`;
  };

  if (hasErrors) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <h3 className="text-sm font-bold text-red-900">
                Systeem Issues Gedetecteerd
              </h3>
              <p className="text-xs text-red-800 whitespace-nowrap">
                {totalErrors} email{totalErrors !== 1 ? 's' : ''} gefaald
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="bg-white rounded p-2 border border-red-200">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Clock className="w-3 h-3 text-red-600" />
                  <span className="text-xs font-medium text-red-900">Laatste uur</span>
                </div>
                <div className="text-lg font-bold text-red-900">{status.failed_last_hour}</div>
              </div>

              <div className="bg-white rounded p-2 border border-red-200">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Clock className="w-3 h-3 text-red-600" />
                  <span className="text-xs font-medium text-red-900">Laatste 24u</span>
                </div>
                <div className="text-lg font-bold text-red-900">{status.failed_last_24h}</div>
              </div>

              <div className="bg-white rounded p-2 border border-red-200">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Clock className="w-3 h-3 text-red-600" />
                  <span className="text-xs font-medium text-red-900">Laatste 7d</span>
                </div>
                <div className="text-lg font-bold text-red-900">{status.failed_last_7d}</div>
              </div>
            </div>

            {status.last_failure_at && (
              <p className="text-xs text-red-700">
                Laatste probleem: {formatTimestamp(status.last_failure_at)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-sm font-bold text-green-900">
            Systeem OK - Geen technische problemen
          </h3>
          <p className="text-xs text-green-700 mt-0.5">
            Laatste check: {formatTimestamp(status.checked_at)}
          </p>
        </div>
      </div>
    </div>
  );
}
